import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useProduct } from '../../contexts/ProductContext';
import LoadingSpinner from '../UI/LoadingSpinner';

const ProductSidebar = () => {
  const { t } = useTranslation();
  const { selectedProduct, isOpen, loading, error, closeProduct } = useProduct();
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (selectedProduct) {
      checkFavoriteStatus();
      setSelectedImage(0); // Reset to first image when product changes
    }
  }, [selectedProduct]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        closeProduct();
      } else if (e.key === 'ArrowLeft' && selectedProduct?.images && selectedProduct.images.length > 1) {
        // Navigate to previous image
        setSelectedImage(prev => 
          prev > 0 ? prev - 1 : selectedProduct.images.length - 1
        );
      } else if (e.key === 'ArrowRight' && selectedProduct?.images && selectedProduct.images.length > 1) {
        // Navigate to next image
        setSelectedImage(prev => 
          prev < selectedProduct.images.length - 1 ? prev + 1 : 0
        );
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeProduct, selectedProduct, selectedImage]);

  const checkFavoriteStatus = () => {
    if (!selectedProduct) return;
    const favorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
    // Convert to strings for comparison to handle both string and number IDs
    setIsFavorite(favorites.map(id => String(id)).includes(String(selectedProduct.id)));
  };

  const toggleFavorite = () => {
    if (!selectedProduct) return;
    const favorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
    
    if (isFavorite) {
      // Use String comparison to handle both string and number IDs
      const newFavorites = favorites.filter(fav => String(fav) !== String(selectedProduct.id));
      localStorage.setItem('favoriteProducts', JSON.stringify(newFavorites));
      setIsFavorite(false);
    } else {
      favorites.push(selectedProduct.id);
      localStorage.setItem('favoriteProducts', JSON.stringify(favorites));
      setIsFavorite(true);
    }
    
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const sidebarVariants = {
    hidden: { 
      x: '100%',
      opacity: 0 
    },
    visible: { 
      x: 0,
      opacity: 1 
    }
  };

  const contentVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.2,
        duration: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: { 
      opacity: 1,
      y: 0
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            className="sidebar-backdrop"
            onClick={closeProduct}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
          
          {/* Sidebar */}
          <motion.div 
            className="product-sidebar"
            variants={sidebarVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ 
              duration: 0.4, 
              ease: [0.25, 0.46, 0.45, 0.94] // Custom easing for smooth motion
            }}
          >
        {/* Header */}
        <div className="sidebar-header">
          <button
            onClick={closeProduct}
            className="close-button"
            aria-label={t('common.close')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="sidebar-content">
          {loading && (
            <motion.div 
              className="flex justify-center items-center h-64"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <LoadingSpinner />
            </motion.div>
          )}

          {error && (
            <motion.div 
              className="p-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-red-500">{error}</p>
            </motion.div>
          )}

          {selectedProduct && !loading && (
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Image Gallery */}
              <motion.div className="sidebar-images" variants={itemVariants}>
                <div className="main-image">
                  <img
                    src={(() => {
                      // Try to get the selected image
                      if (selectedProduct.imagesFull?.[selectedImage]) {
                        // Use the full image object with sizes
                        const img = selectedProduct.imagesFull[selectedImage];
                        return img.sizes?.xlarge?.jpg || img.sizes?.large?.jpg || img.url || selectedProduct.image;
                      } else if (selectedProduct.images?.[selectedImage]) {
                        // Use the simplified images array
                        const img = selectedProduct.images[selectedImage];
                        return typeof img === 'string' ? img : (img?.url || img?.jpg || selectedProduct.image);
                      } else {
                        // Fallback to main image
                        return selectedProduct.imageXl?.jpg || selectedProduct.imageLg?.jpg || selectedProduct.image;
                      }
                    })()}
                    alt={selectedProduct.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {((selectedProduct.imagesFull && selectedProduct.imagesFull.length > 1) || 
                  (selectedProduct.images && selectedProduct.images.length > 1)) && (
                  <div className="image-thumbnails">
                    {(selectedProduct.imagesFull || selectedProduct.images).map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`thumbnail ${selectedImage === idx ? 'active' : ''}`}
                      >
                        <img 
                          src={(() => {
                            if (selectedProduct.imagesFull?.[idx]) {
                              const fullImg = selectedProduct.imagesFull[idx];
                              return fullImg.sizes?.thumbnail?.jpg || fullImg.sizes?.small?.jpg || fullImg.url;
                            } else if (typeof img === 'string') {
                              return img;
                            } else {
                              return img?.url || img?.jpg || selectedProduct.imageThumbnail?.jpg;
                            }
                          })()} 
                          alt={`${selectedProduct.name} ${idx + 1}`} 
                        />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Product Info */}
              <motion.div className="sidebar-info" variants={itemVariants}>
                <div className="product-header">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{selectedProduct.sku}</p>
                    <h2 className="text-2xl font-light text-bmb-text mb-2">{selectedProduct.name}</h2>
                    {selectedProduct.category && (
                      <p className="text-sm text-gray-600">{selectedProduct.category}</p>
                    )}
                  </div>
                  
                  <button
                    onClick={toggleFavorite}
                    className="favorite-button-large"
                    aria-label={isFavorite ? t('product.remove_from_favorites') : t('product.add_to_favorites')}
                  >
                    <svg 
                      className={`w-6 h-6 transition-colors ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-400'}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                      />
                    </svg>
                  </button>
                </div>

                {/* Description */}
                {selectedProduct.description && (
                  <motion.div className="description-section" variants={itemVariants}>
                    <h3 className="text-lg font-medium mb-2">{t('product.description')}</h3>
                    <div 
                      className="text-gray-600 product-description"
                      dangerouslySetInnerHTML={{ __html: selectedProduct.description }}
                    />
                  </motion.div>
                )}

                {/* Materials */}
                {selectedProduct.materials && selectedProduct.materials.length > 0 && (
                  <motion.div className="materials-section" variants={itemVariants}>
                    <h3 className="text-lg font-medium mb-2">{t('product.materials')}</h3>
                    <ul className="space-y-1">
                      {selectedProduct.materials.map((material, idx) => (
                        <li key={idx} className="text-gray-600">• {material}</li>
                      ))}
                    </ul>
                  </motion.div>
                )}

                {/* Details - Only show if there are details to display */}
                {((selectedProduct.weight && selectedProduct.weight > 0) || 
                  selectedProduct.dimensions) && (
                  <motion.div className="details-section" variants={itemVariants}>
                    <h3 className="text-lg font-medium mb-2">{t('product.details')}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {selectedProduct.weight && selectedProduct.weight > 0 && (
                        <div>
                          <span className="text-gray-500">{t('product.weight')}:</span>
                          <span className="ml-2 text-gray-700">{selectedProduct.weight}g</span>
                        </div>
                      )}
                      {selectedProduct.dimensions && (
                        <div>
                          <span className="text-gray-500">{t('product.dimensions')}:</span>
                          <span className="ml-2 text-gray-700">{selectedProduct.dimensions}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Collections */}
                {selectedProduct.collections && selectedProduct.collections.length > 0 && (
                  <motion.div className="collections-section" variants={itemVariants}>
                    <h3 className="text-lg font-medium mb-2">{t('product.collections')}</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedProduct.collections.map((collection, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                          {collection.name}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          )}
        </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProductSidebar;